import React from 'react'
import { Table, Button, Space, Tag, Tooltip, Badge } from 'antd'
import { UserOutlined, FileTextOutlined, EyeOutlined } from '@ant-design/icons'
import { formatCurrency } from '../../../utils/helpers'
import dayjs from 'dayjs'

const PatientAccountsTable = ({ patients, loading, pagination, onViewAccount, onNewInvoice, onPageChange }) => {
  const getStatusColor = (status) => {
    const statusColors = {
      active: 'green',
      awaiting_payment_patient: 'orange',
      awaitreaiting_payment_aid: 'blue',
      disputed: 'red',
      completed: 'green',
      payment_plan: 'purple'
    }
    return statusColors[status] || 'default'
  }

  const getAgingColor = (days) => {
    if (days >= 90) return '#ff4d4f'
    if (days >= 60) return '#fa8c16'
    if (days >= 30) return '#faad14'
    return '#52c41a'
  }

  const columns = [
    {
      title: 'Account No',
      dataIndex: 'billingId',
      key: 'billingId',
      width: 120,
      fixed: 'left',
      render: (text) => (
        <Tooltip title={`Account Number: ${text}`}>
          <Tag color='blue' className='billing-table-tag billing-table-tag-with-tooltip'>
            {text}
          </Tag>
        </Tooltip>
      ),
      sorter: (a, b) => a.billingId.localeCompare(b.billingId)
    },
    {
      title: 'Patient Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left',
      render: (text, record) => (
        <div className='billing-patient-cell'>
          <UserOutlined style={{ fontSize: '16px', color: '#1890ff', flexShrink: 0 }} />
          <div className='billing-patient-info'>
            <Tooltip title={text} placement='topLeft'>
              <div className='billing-patient-name'>{text}</div>
            </Tooltip>
            <div className='billing-patient-details'>
              {record.idNo && (
                <Tooltip title={`ID: ${record.idNo}`}>
                  <span className='billing-patient-detail-item'>ID: {record.idNo}</span>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'ID Number',
      dataIndex: 'idNo',
      key: 'idNo',
      width: 130,
      render: (text) => (
        <Tooltip title={text} placement='topLeft'>
          <div className='billing-table-cell-ellipsis'>{text || 'N/A'}</div>
        </Tooltip>
      )
    },
    {
      title: 'Medical Aid',
      dataIndex: 'medicalAid',
      key: 'medicalAid',
      width: 150,
      render: (text) => (
        <div className='billing-table-tags-container'>
          <Tooltip title={`Medical Aid: ${text}`}>
            <Tag
              color={text === 'Private' ? 'default' : 'cyan'}
              className='billing-table-tag billing-table-tag-with-tooltip'
            >
              {text}
            </Tag>
          </Tooltip>
        </div>
      )
    },
    {
      title: 'Total Outstanding',
      dataIndex: 'totalOutstanding',
      key: 'totalOutstanding',
      width: 150,
      align: 'right',
      render: (amount) => (
        <Tooltip title={`Total Outstanding: ${formatCurrency(amount)}`} placement='topLeft'>
          <div
            className='billing-currency'
            style={{
              color: amount > 0 ? '#ff4d4f' : '#52c41a',
              fontWeight: 'bold'
            }}
          >
            {formatCurrency(amount)}
          </div>
        </Tooltip>
      ),
      sorter: (a, b) => a.totalOutstanding - b.totalOutstanding
    },
    {
      title: 'Medical Aid Due',
      dataIndex: 'medicalAidOutstanding',
      key: 'medicalAidOutstanding',
      width: 150,
      align: 'right',
      render: (amount) => (
        <Tooltip title={`Amount owed by medical aid: ${formatCurrency(amount)}`} placement='topLeft'>
          <div className='billing-currency' style={{ color: '#1890ff' }}>
            {formatCurrency(amount)}
          </div>
        </Tooltip>
      )
    },
    {
      title: 'Patient Due',
      dataIndex: 'patientOutstanding',
      key: 'patientOutstanding',
      width: 150,
      align: 'right',
      render: (amount) => (
        <Tooltip title={`Amount owed by patient: ${formatCurrency(amount)}`} placement='topLeft'>
          <div className='billing-currency' style={{ color: '#fa8c16' }}>
            {formatCurrency(amount)}
          </div>
        </Tooltip>
      )
    },
    {
      title: 'Days Outstanding',
      dataIndex: 'daysOutstanding',
      key: 'daysOutstanding',
      width: 130,
      align: 'center',
      render: (days) => (
        <Tooltip title={`${days} days outstanding`}>
          <Badge count={days} style={{ backgroundColor: getAgingColor(days) }} overflowCount={999} />
        </Tooltip>
      ),
      sorter: (a, b) => a.daysOutstanding - b.daysOutstanding
    },
    {
      title: 'Status',
      dataIndex: 'accountStatus',
      key: 'accountStatus',
      width: 150,
      render: (status) => {
        const statusLabels = {
          active: 'Active',
          awaiting_payment_patient: 'Awaiting Patient',
          awaiting_payment_aid: 'Awaiting Aid',
          disputed: 'Disputed',
          completed: 'Completed',
          payment_plan: 'Payment Plan'
        }
        const statusLabel = statusLabels[status] || status
        return (
          <div className='billing-table-tags-container'>
            <Tooltip title={`Status: ${statusLabel}`}>
              <Tag color={getStatusColor(status)} className='billing-table-tag billing-table-tag-with-tooltip'>
                {statusLabel}
              </Tag>
            </Tooltip>
          </div>
        )
      }
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastInvoiceDate',
      key: 'lastInvoiceDate',
      width: 120,
      render: (date) => {
        const formattedDate = date ? dayjs(date).format('DD MMM YYYY') : 'N/A'
        return (
          <Tooltip title={`Last Activity: ${formattedDate}`} placement='topLeft'>
            <div className='billing-table-cell-ellipsis' style={{ fontSize: '12px' }}>
              {formattedDate}
            </div>
          </Tooltip>
        )
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <div className='billing-table-actions'>
          <Tooltip title='View Account Details'>
            <Button type='primary' size='small' icon={<EyeOutlined />} onClick={() => onViewAccount(record)} />
          </Tooltip>
          <Tooltip title='Create Invoice'>
            <Button size='small' icon={<FileTextOutlined />} onClick={() => onNewInvoice(record)} />
          </Tooltip>
        </div>
      )
    }
  ]

  return (
    <Table
      className='billing-module-table'
      columns={columns}
      dataSource={patients}
      loading={loading}
      scroll={{ x: 1800 }}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} patients`,
        pageSizeOptions: ['10', '20', '50', '100'],
        onChange: onPageChange
      }}
      size='small'
      bordered={false}
      rowClassName={(record, index) => (index % 2 === 0 ? 'billing-table-row-even' : 'billing-table-row-odd')}
    />
  )
}

export default PatientAccountsTable
